// Storage backend interface (save may be sync or async; SuperSaveSystem normalizes with Promise.resolve)
export interface StorageBackend {
  save(key: string, data: any): void | Promise<void>;
  load(key: string, initialState: any): Promise<any>;
  clear(): void | Promise<void>;
}

/** GemShell/local file content may be an object or a JSON string; game code expects a parsed object. */
function normalizeLoadedData(data: unknown, initialState: any): any {
  if (data === null || data === undefined) {
    return initialState;
  }
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      if (parsed === null || parsed === undefined) {
        return initialState;
      }
      return parsed;
    } catch {
      return initialState;
    }
  }
  return data;
}

// LocalStorage backend implementation
export class LocalStorageBackend implements StorageBackend {
  save(key: string, data: any): void {
    localStorage.setItem(key, JSON.stringify(data));
  }

  load(key: string, initialState: any): Promise<any> {
    const rawString = localStorage.getItem(key) || JSON.stringify(initialState) || '{}';
    return Promise.resolve(normalizeLoadedData(JSON.parse(rawString), initialState));
  }

  clear(): void {
    Object.keys(localStorage).forEach((storageKey) => {
      if (!storageKey.includes('global-settings')) {
        localStorage.removeItem(storageKey);
      }
    });
  }
}

// Gemshell backend implementation — must await native I/O so Zustand persist and await save() actually flush.
export class GemshellBackend implements StorageBackend {
  async save(key: string, data: any): Promise<void> {
    if (data === null || data === undefined) {
      await gemshell?.file.delete(key);
      return;
    }
    await gemshell?.file.save(key, data);
  }

  async load(key: string, initialState: any): Promise<any> {
    try {
      const data = await gemshell?.file.load(key);
      if (data === null || data === undefined) {
        return initialState;
      }
      return normalizeLoadedData(data, initialState);
    } catch {
      return initialState;
    }
  }

  async clear(): Promise<void> {
    const keys = await gemshell?.file.list();
    if (!keys) return;

    await Promise.all(
      keys
        .filter((saveKey) => !saveKey.includes('global-settings'))
        .map((saveKey) => gemshell?.file.delete(saveKey)),
    );
  }
}
